import { useEffect, useRef } from "react";

// Loads the YouTube IFrame Player API exactly once and resolves with `window.YT`.
let apiPromise;
const loadYouTubeAPI = () => {
  if (apiPromise) return apiPromise;
  apiPromise = new Promise((resolve) => {
    if (window.YT && window.YT.Player) return resolve(window.YT);
    const previous = window.onYouTubeIframeAPIReady;
    window.onYouTubeIframeAPIReady = () => {
      if (previous) previous();
      resolve(window.YT);
    };
    const tag = document.createElement("script");
    tag.src = "https://www.youtube.com/iframe_api";
    document.body.appendChild(tag);
  });
  return apiPromise;
};

// Pulls the 11-char video id out of an /embed/<id> URL (ignoring ?si=… etc.).
const extractVideoId = (url = "") => {
  const match = url.match(/\/embed\/([^/?#]+)/);
  return match ? match[1] : url;
};

// Drag sensitivity (degrees of view rotation per pixel dragged).
const SENSITIVITY = 0.18;
// Movement (px) below which a pointer interaction counts as a click, not a drag.
const CLICK_THRESHOLD = 4;

/**
 * YouTube 360° video whose viewing angle is controlled by mouse/touch drag.
 *
 * The native drag-to-look on YouTube 360 embeds is unreliable inside third-party
 * pages, so we drive it ourselves through the IFrame Player API: a transparent
 * overlay captures the drag and feeds yaw/pitch to `setSphericalProperties`.
 * A drag that doesn't move (a click) toggles play/pause.
 */
export const Video360 = ({ src, title, className = "" }) => {
  const targetRef = useRef(null); // replaced by the <iframe> the API creates
  const playerRef = useRef(null);
  const dragRef = useRef(null);

  useEffect(() => {
    let destroyed = false;
    loadYouTubeAPI().then((YT) => {
      if (destroyed || !targetRef.current) return;
      playerRef.current = new YT.Player(targetRef.current, {
        videoId: extractVideoId(src),
        playerVars: { playsinline: 1, rel: 0 },
      });
    });
    return () => {
      destroyed = true;
      if (playerRef.current && playerRef.current.destroy) {
        playerRef.current.destroy();
      }
    };
  }, [src]);

  // Reads yaw/pitch into `drag`; `fov` is only present once a spherical video is
  // actually playing, so this is how we know panning is available yet.
  const primeSpherical = (drag, clientX, clientY) => {
    const player = playerRef.current;
    const props = player && player.getSphericalProperties && player.getSphericalProperties();
    if (!props || props.fov === undefined) return false;
    drag.spherical = true;
    drag.x = clientX;
    drag.y = clientY;
    drag.yaw = props.yaw;
    drag.pitch = props.pitch;
    return true;
  };

  const handlePointerDown = (e) => {
    if (!playerRef.current) return;
    const drag = { x: e.clientX, y: e.clientY, spherical: false, moved: false };
    primeSpherical(drag, e.clientX, e.clientY);
    dragRef.current = drag;
    e.currentTarget.setPointerCapture(e.pointerId);
  };

  const handlePointerMove = (e) => {
    const drag = dragRef.current;
    if (!drag) return;
    if (Math.abs(e.clientX - drag.x) > CLICK_THRESHOLD || Math.abs(e.clientY - drag.y) > CLICK_THRESHOLD) {
      drag.moved = true;
    }
    // The video may only become spherical after it starts playing mid-interaction.
    if (!drag.spherical && !primeSpherical(drag, e.clientX, e.clientY)) return;
    // Dragging left turns the view right (and vice versa).
    const yaw = (((drag.yaw + (e.clientX - drag.x) * SENSITIVITY) % 360) + 360) % 360;
    const pitch = Math.max(-90, Math.min(90, drag.pitch + (e.clientY - drag.y) * SENSITIVITY));
    playerRef.current.setSphericalProperties({ yaw, pitch });
  };

  const handlePointerUp = (e) => {
    const drag = dragRef.current;
    dragRef.current = null;
    if (e.currentTarget.releasePointerCapture) {
      e.currentTarget.releasePointerCapture(e.pointerId);
    }
    // A tap/click (no meaningful drag) toggles playback.
    if (drag && !drag.moved && playerRef.current) {
      const state = playerRef.current.getPlayerState();
      if (state === window.YT.PlayerState.PLAYING) {
        playerRef.current.pauseVideo();
      } else {
        playerRef.current.playVideo();
      }
    }
  };

  return (
    <div className={`tw:relative tw:w-full tw:aspect-video ${className}`}>
      <div ref={targetRef} title={title} className="tw:absolute tw:inset-0 tw:w-full tw:h-full" />
      <div
        className="tw:absolute tw:inset-0 tw:cursor-grab tw:active:cursor-grabbing tw:touch-none"
        onPointerDown={handlePointerDown}
        onPointerMove={handlePointerMove}
        onPointerUp={handlePointerUp}
        onPointerCancel={handlePointerUp}
        role="application"
        aria-label={title}
      />
    </div>
  );
};
